import { useAppointmentModal } from 'helpers/Context/AppointmentModalProvider';
import { useProvidersByLocation } from 'helpers/Coveo/getProvidersByLocation';
import React, { useRef } from 'react';

interface MyChartProps {
  vt: string;
  epicID: string;
  typeOfItem: string;
  additionalParams?: {
    key: string;
    value: string | number | object;
  }[];
}

const MyChart = React.memo(function MyChart(props: MyChartProps) {
  const { vt, epicID, typeOfItem, additionalParams } = props;
  const [scriptLoaded, setScriptLoaded] = React.useState(false);
  const [combainedEpicID, setCombainedEpicID] = React.useState<string>('');
  const containerRef = useRef(null);
  const containerId = 'OpenSchedulingContainer';
  const { itemName } = useAppointmentModal();

  const providersList = useProvidersByLocation(itemName?.toLowerCase() || '');

  const apiKey = process.env.NEXT_PUBLIC_MYCHART_APT_ID;
  const url = process.env.NEXT_PUBLIC_MYCHART_URL;
  const widgetType = process.env.NEXT_PUBLIC_MYCHART_WIDGET_TYPE;
  const widgetSDKLink = process.env.NEXT_PUBLIC_MYCHART_WIDGETSDK_LINK;

  React.useEffect(() => {
    if (typeOfItem === 'location') {
      const combainedEpicID = providersList?.filter((provider) => provider?.raw?.cwepicid) || '';
      const filteredCombainedEpicID =
        combainedEpicID?.map((provider) => provider?.raw?.cwepicid)?.join(',') || '';

      const dubalicateFilteredCombainedEpicID = filteredCombainedEpicID
        .split(',')
        .filter((id, index, self) => self.indexOf(id) === index)
        .join(',');
      setCombainedEpicID(dubalicateFilteredCombainedEpicID);
    } else {
      const dubalicateFilteredEpicID = epicID
        .split(',')
        .filter((id, index, self) => self.indexOf(id) === index)
        .join(',');
      setCombainedEpicID(dubalicateFilteredEpicID);
    }
  }, [providersList, epicID, typeOfItem]);

  // Load script and handle cleanup
  React.useEffect(() => {
    // Only load script if it's not already loaded
    if (!document.getElementById('my-widget-script')) {
      const script = document.createElement('script');
      script.id = 'my-widget-script';
      script.src = widgetSDKLink || '';
      script.async = true;
      script.onload = () => setScriptLoaded(true);
      document.body.appendChild(script);
    } else {
      setScriptLoaded(true);
    }

    return () => {
      // Only cleanup when component unmounts
      if (!document.getElementById(containerId)) {
        document
          .querySelectorAll('iframe[id^="MyChartIframe"]')
          .forEach((iframe) => iframe.remove());
        ['my-widget-script', 'MyChartWidgetScript', 'MyChartWidgetPolyfillScript'].forEach((id) => {
          document.getElementById(id)?.remove();
        });
        window.mychartWidget = undefined;
        window.WidgetSDK = undefined;
        setScriptLoaded(false);
      }
    };
  }, [widgetSDKLink]);

  // Memoize additionalParams to prevent unnecessary re-renders
  const processedParams = React.useMemo(() => {
    if (!additionalParams) return {};
    return additionalParams.reduce(
      (acc, { key, value }) => ({
        ...acc,
        [`${key.replace(/^data-(?:additionalparams-)?/, '')}`]:
          typeof value === 'object' ? JSON.stringify(value) : String(value),
      }),
      {}
    );
  }, [additionalParams]);

  // Memoize widget configuration
  const widgetConfig = React.useMemo(
    () => ({
      url,
      apiKey,
      widgetType,
      containerId,
      additionalParams: {
        vt,
        ...(typeOfItem === 'location' ? { dept: epicID } : {}),
        ...(combainedEpicID ? { id: combainedEpicID } : {}),
        ...(vt === '411'
          ? { selprovid: typeOfItem === 'provider' ? epicID : combainedEpicID }
          : {}),
        ...processedParams,
      },
    }),
    [url, apiKey, widgetType, vt, typeOfItem, epicID, combainedEpicID, processedParams]
  );

  // Initialize widget after script loads and container exists
  React.useEffect(() => {
    if (!scriptLoaded || !containerRef.current) return;

    // Wait for combainedEpicID to be set
    if (typeOfItem === 'location' && !combainedEpicID) {
      return;
    }

    let isMounted = true;
    const initTimer = setTimeout(() => {
      if (!isMounted) return;

      const container = document.getElementById(containerId);
      if (container?.tagName === 'DIV' && window.mychartWidget?.renderWidget) {
        // Clean up any existing iframes first
        const existingIframe = container.querySelector('iframe');
        if (existingIframe) {
          existingIframe.remove();
        }

        window.mychartWidget.renderWidget(widgetConfig);
      }
    }, 100);

    return () => {
      isMounted = false;
      clearTimeout(initTimer);
    };
  }, [scriptLoaded, widgetConfig, typeOfItem, combainedEpicID]);

  if (!vt) {
    return (
      <div>
        <strong>vt is Missing!</strong>
      </div>
    );
  }

  if (!scriptLoaded) {
    return <div>Loading scheduling widget...</div>;
  }

  return (
    <div className="w-full">
      <div
        ref={containerRef}
        id={containerId}
        data-url={url}
        data-apikey={apiKey}
        data-widget-type={widgetType}
        data-additionalparams-vt={vt}
        {...(typeOfItem === 'location' ? { 'data-additionalparams-dept': epicID } : {})}
        {...(combainedEpicID ? { 'data-additionalparams-id': combainedEpicID } : {})}
        {...(vt === '411'
          ? {
              'data-additionalparams-selprovid':
                typeOfItem === 'provider' ? epicID : combainedEpicID,
            }
          : {})}
        {...additionalParams?.reduce(
          (acc, { key, value }) => ({
            ...acc,
            [key]: typeof value === 'object' ? JSON.stringify(value) : String(value),
          }),
          {}
        )}
      ></div>
    </div>
  );
});

export default MyChart;
